import { ServiceSupportedCountry } from '@app/modules/service/models/service-supported-country.model';
import { ServiceEnum } from '@generated/graphql';
import { JobServicesOrder } from '../models/job.model';

type ServiceToggleType = 'add' | 'remove';
type OrderServiceType = 'mainServices' | 'additionalServices';

export type OrderServiceState = {
  jobServicesOrder: JobServicesOrder;
  incorporationServices: ServiceSupportedCountry[];
  addOrUpdateService: (
    serviceName: ServiceEnum,
    type: OrderServiceType,
    quantity?: number,
  ) => OrderServiceState;
  removeService: (
    serviceName: ServiceEnum,
    type: OrderServiceType,
  ) => OrderServiceState;
  resetMainServices: () => OrderServiceState;
  resetAdditionalServices: () => OrderServiceState;
  resetServices: () => OrderServiceState;
  addOrUpdateCustomService: (
    serviceSupportedCountry: ServiceSupportedCountry,
    quantity?: number,
  ) => OrderServiceState;
  removeCustomService: (
    serviceSupportedCountry: ServiceSupportedCountry,
  ) => OrderServiceState;
  toggleRegisteredAddressService: (
    type: ServiceToggleType,
  ) => OrderServiceState;
  toggleUserNomineeService: (type: ServiceToggleType) => OrderServiceState;
};

export function createOrderServiceState(): OrderServiceState {
  const state: OrderServiceState = {
    jobServicesOrder: {
      mainServices: [],
      additionalServices: [],
    },
    incorporationServices: [],
    addOrUpdateService: (
      serviceName: ServiceEnum,
      type: OrderServiceType,
      quantity = 1,
    ) => {
      const existsServiceIndex = state.jobServicesOrder[type].findIndex(
        (js) => js.service.service.name === serviceName,
      );

      const service = state.incorporationServices.find(
        (supportedService) => supportedService.service.name === serviceName,
      );

      if (!service) {
        return state;
      }

      if (existsServiceIndex > -1) {
        state.jobServicesOrder[type][existsServiceIndex] = {
          quantity,
          service,
        };
      } else {
        state.jobServicesOrder[type].push({
          quantity,
          service,
        });
      }

      return state;
    },
    removeService: (serviceName: ServiceEnum, type: OrderServiceType) => {
      state.jobServicesOrder[type] = state.jobServicesOrder[type].filter(
        (js) => js.service.service.name !== serviceName,
      );

      return state;
    },
    resetMainServices: () => {
      state.jobServicesOrder.mainServices = [];

      return state;
    },
    resetAdditionalServices: () => {
      state.jobServicesOrder.additionalServices = [];

      return state;
    },
    resetServices: () => {
      state.resetMainServices().resetAdditionalServices();

      return state;
    },
    addOrUpdateCustomService: (
      serviceSupportedCountry: ServiceSupportedCountry,
      quantity = 1,
    ) => {
      const type = 'additionalServices';
      const existsServiceIndex = state.jobServicesOrder[type].findIndex(
        (js) => js.service.id === serviceSupportedCountry.id,
      );

      const service = state.incorporationServices.find(
        (supportedService) => supportedService.id === serviceSupportedCountry.id,
      );

      if (!service) {
        return state;
      }

      if (existsServiceIndex > -1) {
        state.jobServicesOrder[type][existsServiceIndex] = {
          quantity,
          service,
        };
      } else {
        state.jobServicesOrder[type].push({
          quantity,
          service,
        });
      }

      return state;
    },
    removeCustomService: (serviceSupportedCountry: ServiceSupportedCountry) => {
      const type = 'additionalServices';

      state.jobServicesOrder[type] = state.jobServicesOrder[type].filter(
        (js) => js.service.id !== serviceSupportedCountry.id,
      );

      return state;
    },
    toggleRegisteredAddressService: (type: ServiceToggleType) => {
      if (type === 'add') {
        return state.addOrUpdateService(
          ServiceEnum.RegisteredAddress,
          'additionalServices',
        );
      }

      return state.removeService(
        ServiceEnum.RegisteredAddress,
        'additionalServices',
      );
    },
    toggleUserNomineeService: (type: ServiceToggleType) => {
      if (type === 'add') {
        return state.addOrUpdateService(
          ServiceEnum.NomineeDirector,
          'additionalServices',
        );
      }

      return state.removeService(
        ServiceEnum.NomineeDirector,
        'additionalServices',
      );
    },
  };

  return state;
}
